"use client";

import { useEffect, useRef, useState } from "react";
import { AlertCircle } from "lucide-react";

import { MarkdownCodeBlock } from "./markdown-code-block";

type RenderState =
  | { status: "idle" }
  | {
      status: "success";
      requestCode: string;
      svg: string;
      bindFunctions?: (element: Element) => void;
    }
  | {
      status: "error";
      requestCode: string;
      message: string;
    };

type MarkdownMermaidBlockProps = {
  code: string;
};

export function MarkdownMermaidBlock({ code }: MarkdownMermaidBlockProps) {
  const [state, setState] = useState<RenderState>({ status: "idle" });
  const diagramCounterRef = useRef(0);
  const diagramContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    diagramCounterRef.current += 1;
    const requestCode = code;
    const diagramId = `markdown-mermaid-${diagramCounterRef.current}`;

    async function renderDiagram() {
      try {
        const mermaid = await loadMermaid();
        const { svg, bindFunctions } = await mermaid.render(diagramId, code);

        if (!cancelled) {
          setState({ status: "success", svg, bindFunctions, requestCode });
        }
      } catch (error) {
        if (!cancelled) {
          setState({ status: "error", message: formatError(error), requestCode });
        }
      }
    }

    void renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [code]);

  const isCurrentResult = state.status !== "idle" && state.requestCode === code;

  const bindFunctions =
    state.status === "success" && isCurrentResult ? state.bindFunctions : undefined;

  const isError = state.status === "error" && isCurrentResult;
  const isSuccess = state.status === "success" && isCurrentResult;

  useEffect(() => {
    if (!bindFunctions) {
      return;
    }
    if (diagramContainerRef.current) {
      bindFunctions(diagramContainerRef.current);
    }
  }, [bindFunctions]);

  if (isError && state.status === "error") {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-3 rounded-[var(--radius-xl)] border border-destructive/40 bg-destructive/10 px-4 py-3 text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4" aria-hidden />
          <div className="space-y-1">
            <p className="font-semibold">Unable to render Mermaid diagram</p>
            <p className="text-sm text-destructive/80">Showing the original source below.</p>
            <p className="text-xs text-destructive/70">{state.message}</p>
          </div>
        </div>
        <MarkdownCodeBlock code={code} language="mermaid" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[var(--radius-xxl)] border border-border/70 bg-[color-mix(in_oklab,var(--token-surface-muted)_90%,transparent)]">
      <div className="max-h-[540px] overflow-auto px-4 py-6">
        {isSuccess && state.status === "success" ? (
          <div
            ref={diagramContainerRef}
            className="mermaid-diagram"
            dangerouslySetInnerHTML={{ __html: state.svg }}
          />
        ) : (
          <div className="flex min-h-32 items-center justify-center text-sm text-muted-foreground">
            Rendering diagram…
          </div>
        )}
      </div>
    </div>
  );
}

type MermaidAPI = typeof import("mermaid") extends { default: infer T } ? T : never;

let mermaidInstancePromise: Promise<MermaidAPI> | null = null;

async function loadMermaid() {
  if (!mermaidInstancePromise) {
    mermaidInstancePromise = import("mermaid").then((module) => {
      const mermaid = module.default;
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: "strict",
      });
      return mermaid;
    });
  }
  return mermaidInstancePromise;
}

function formatError(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Unknown Mermaid rendering error.";
}
